import { FC, useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
} from '@chakra-ui/react';
import { LuArrowLeft, LuDownload } from 'react-icons/lu';
import { AIAnalysisHistory, AIAnalysisHistoryFilters } from '@/types/aiManagement';
import { Dog } from '@/types/dog';
import { aiAnalysisHistoryService } from '@/services/firebase/aiAnalysisHistoryService';
import { dogService } from '@/services/firebase/dogService';
import { HistoryFilters, HistoryTable } from '@/components/aiHistory';
import { useAuthStore } from '@/stores/authStore';
import { DocumentSnapshot } from 'firebase/firestore';
import { AppLayout } from '@/components/layout/AppLayout';

const PAGE_SIZE = 20;

/**
 * AI分析履歴ページ
 */
export const AIAnalysisHistoryPage: FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const initialFetchRef = useRef(false);
  const filtersRef = useRef<AIAnalysisHistoryFilters>({});

  // 履歴データ
  const [histories, setHistories] = useState<AIAnalysisHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [lastDoc, setLastDoc] = useState<DocumentSnapshot | null>(null);

  // フィルター
  const [filters, setFilters] = useState<AIAnalysisHistoryFilters>({});

  // メス犬一覧（フィルター用）
  const [femaleDogs, setFemaleDogs] = useState<Dog[]>([]);

  // エクスポート中
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (user && !initialFetchRef.current) {
      initialFetchRef.current = true;
      fetchFemaleDogs();
      fetchHistories(true);
    }
  }, [user]);

  // フィルター変更時の再取得
  useEffect(() => {
    if (user && initialFetchRef.current && JSON.stringify(filters) !== JSON.stringify(filtersRef.current)) {
      filtersRef.current = filters;
      fetchHistories(true);
    }
  }, [filters]);

  const fetchFemaleDogs = async () => {
    if (!user) return;

    try {
      const dogs = await dogService.getDogs(user.uid, { gender: 'female' });
      setFemaleDogs(dogs);
    } catch (error) {
      console.error('メス犬取得エラー:', error);
    }
  };

  const fetchHistories = async (reset = false) => {
    if (!user) return;

    try {
      if (reset) {
        setLoading(true);
        setHistories([]);
        setLastDoc(null);
      } else {
        setLoadingMore(true);
      }

      const result = await aiAnalysisHistoryService.getHistories(
        user.uid,
        filters,
        {
          limit: PAGE_SIZE,
          startAfterDoc: reset ? undefined : lastDoc,
        }
      );

      if (reset) {
        setHistories(result.histories);
      } else {
        setHistories((prev) => [...prev, ...result.histories]);
      }

      setLastDoc(result.lastDoc as DocumentSnapshot | null);
      setHasMore(result.hasMore);
    } catch (error) {
      console.error('履歴取得エラー:', error);
      alert('履歴の取得に失敗しました');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      fetchHistories(false);
    }
  }, [loadingMore, hasMore, lastDoc, filters]);

  const handleFilterChange = (newFilters: AIAnalysisHistoryFilters) => {
    setFilters(newFilters);
  };

  const handleViewDetail = (history: AIAnalysisHistory) => {
    navigate(`/admin/analysis-history/${history.id}`);
  };

  const handleExportCSV = async () => {
    if (!user) return;

    try {
      setExporting(true);

      const exportHistories = await aiAnalysisHistoryService.getHistoriesForExport(
        user.uid,
        {
          startDate: filters.startDate,
          endDate: filters.endDate,
        }
      );

      const csvContent = aiAnalysisHistoryService.convertToCSV(exportHistories);

      // CSVダウンロード
      const blob = new Blob(['\uFEFF' + csvContent], {
        type: 'text/csv;charset=utf-8',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ai_analysis_history_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('エクスポートエラー:', error);
      alert('エクスポートに失敗しました');
    } finally {
      setExporting(false);
    }
  };

  return (
    <AppLayout>
      <VStack align="stretch" gap={6}>
        {/* ヘッダー */}
        <HStack justify="space-between">
          <HStack gap={4}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/ai-mating')}
            >
              <LuArrowLeft />
            </Button>
            <Text fontSize="2xl" fontWeight="bold">
              AI分析履歴
            </Text>
          </HStack>
          <Button
            variant="outline"
            onClick={handleExportCSV}
            loading={exporting}
            disabled={exporting || histories.length === 0}
          >
            <LuDownload />
            CSVエクスポート
          </Button>
        </HStack>

        {/* フィルター */}
        <HistoryFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          femaleDogs={femaleDogs}
        />

        {/* 履歴テーブル */}
        <Box bg="white" borderRadius="lg" shadow="sm" overflow="hidden">
          <HistoryTable
            histories={histories}
            loading={loading}
            onViewDetail={handleViewDetail}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            loadingMore={loadingMore}
          />
        </Box>
      </VStack>
    </AppLayout>
  );
};

export default AIAnalysisHistoryPage;
